import {Schema} from "effect";

// ── Shared configuration type ───────────────────────────────────────────

/**
 * Tunable parameters that fully describe a short-ID variant.
 *
 * Two different companions (e.g. project-scoped {@link ShortId} and
 * account-scoped `AccountShortId`) use the same core algorithm but with
 * distinct configs, giving them different salt namespaces, minimum
 * lengths, etc.
 *
 * The schema layer reads the pattern-shape fields (alphabet, minLength,
 * prefix) to build refinement patterns. The logic layer
 * (`@buildwithflux/identifiers-logic`) reads the algorithm fields
 * (hmacKey, feistelRounds, radix) to drive the Feistel FPE network.
 */
interface ShortIdConfig {
    readonly alphabet: string;
    readonly radix: number;
    readonly prefix: string;
    readonly minLength: number;
    readonly hmacKey: string;
    readonly feistelRounds: number;
}

function validateConfig(config: ShortIdConfig, label: string): void {
    if (config.alphabet.includes(config.prefix)) {
        throw new Error(
            `${label} config error: prefix '${config.prefix}' must not be in alphabet`,
        );
    }
    if (config.radix !== config.alphabet.length) {
        throw new Error(
            `${label} config error: radix (${config.radix}) must equal alphabet length (${config.alphabet.length})`,
        );
    }
}

/**
 * Build the pattern validating raw strings as branded short IDs for the
 * given config. Exported as a helper so each companion can build a
 * matching schema from its own config.
 */
function buildShortIdPattern(cfg: ShortIdConfig): RegExp {
    return new RegExp(`^[${cfg.alphabet}]{${cfg.minLength},}$`);
}

// ── Project ShortId config ──────────────────────────────────────────────

const config: ShortIdConfig = {
    alphabet: "abcdefghijklmnopqrstuvwxyz0123456789",
    radix: 36,
    prefix: "~",
    minLength: 2,
    hmacKey: "flux-short-id-v1",
    feistelRounds: 8,
};

// ── Branded type via Effect Schema ──────────────────────────────────────

const shortIdPattern = buildShortIdPattern(config);

const schema = Schema.String.pipe(Schema.pattern(shortIdPattern), Schema.brand("ShortId"));

type ShortId = typeof schema.Type;

// ── ShortIdData — richer return type storing value + input metadata ─────

/**
 * Richer short ID type that stores the generated value alongside the
 * scope UID and monotonic counter that produced it. This gives consumers
 * all input data needed to recreate or verify the short ID.
 *
 * The `ScopeUid` type parameter acts as a compile-time witness so that
 * e.g. a project short ID is `ShortIdData<AccountUid>` while a
 * project-item short ID is `ShortIdData<DocumentUid>`.
 *
 * The optional `Value` type parameter carries the branded string type
 * (e.g. `ShortId` or `AccountShortId`) so different companions can
 * produce nominally distinct data types.
 */
interface ShortIdData<ScopeUid extends string = string, Value extends string = ShortId> {
    /** The generated short ID string (branded). */
    readonly value: Value;
    /** The scope UID that was used to derive the salt. */
    readonly scopeUid: ScopeUid;
    /** The monotonic counter value that was permuted. */
    readonly counter: number;
}

// ── Schema-only companion ──────────────────────────────────────────────

/**
 * Shape of the schema-layer portion of a short-ID companion: the config,
 * the branded Effect Schema, and the refinement-isomorphism helpers for
 * plain strings. No crypto, no generation.
 *
 * The full companion (with `.generate()`, `.computeSalt()`, etc.) lives
 * in `@buildwithflux/identifiers-logic` and extends this shape.
 */
interface ShortIdSchemaCompanion<Value extends string> {
    readonly config: ShortIdConfig;
    readonly schema: Schema.Schema<Value, string>;
    readonly pattern: RegExp;
    readonly fromString: (raw: string) => Value;
    readonly fromStringSafe: (raw: string) => Value | undefined;
}

/**
 * Create the schema-layer portion of a short-ID companion bound to the
 * given config and branded schema.
 *
 * The `label` is used both as the Effect `Schema.brand` tag source of
 * truth and in error messages when parsing raw strings. Validation of
 * the config is performed eagerly so configuration mistakes surface at
 * module load time.
 */
function createShortIdSchemaCompanion<Value extends string>(
    cfg: ShortIdConfig,
    schemaForCompanion: Schema.Schema<Value, string>,
    label: string,
): ShortIdSchemaCompanion<Value> {
    validateConfig(cfg, label);
    const pattern = buildShortIdPattern(cfg);

    const fromString = (raw: string): Value => {
        if (!pattern.test(raw)) {
            throw new Error(`Invalid ${label}: '${raw}'`);
        }
        return raw as Value;
    };

    const fromStringSafe = (raw: string): Value | undefined => {
        if (!pattern.test(raw)) {
            return undefined;
        }
        return raw as Value;
    };

    return {
        config: cfg,
        schema: schemaForCompanion,
        pattern,
        fromString,
        fromStringSafe,
    };
}

// ── Project ShortId companion (schema-only) ─────────────────────────────

/**
 * Schema-only project {@link ShortIdSchemaCompanion}. Exposes the brand
 * schema, pattern, and refinement-isomorphism helpers. No crypto.
 *
 * To generate new project ShortIds, import the full companion of the
 * same name from `@buildwithflux/identifiers-logic`.
 */
const ShortId: ShortIdSchemaCompanion<ShortId> = createShortIdSchemaCompanion(config, schema, "ShortId");

export {ShortId, buildShortIdPattern, createShortIdSchemaCompanion};
export type {ShortId as ShortIdType, ShortIdData, ShortIdConfig, ShortIdSchemaCompanion};
